/**
 * Shared formatting utilities for the human-readable detail view.
 * BR-11: No ISO timestamps, no camelCase labels, no raw IDs.
 */

import { ConfigurationObject } from '../../services/configs-api';

/**
 * Formats an ISO timestamp to human-readable: "14 May 2026, 12:39 pm"
 */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }) + ', ' + date.toLocaleTimeString('en-NZ', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Resolves a configId to its human-readable configName.
 * Returns the configName if found, or the raw ID with a warning prefix if not.
 */
export function resolveConfigName(configId: string | undefined, allConfigs: ConfigurationObject[]): string {
  if (!configId) return '—';
  const found = allConfigs.find((c) => c.configId === configId);
  return found ? found.configName : `⚠ ${configId} (not found)`;
}

/**
 * Polly voice metadata for human-readable display.
 */
export const POLLY_VOICES: Record<string, { language: string; gender: string; flag: string }> = {
  Aria: { language: 'English (New Zealand)', gender: 'Female', flag: '🇳🇿' },
  Russell: { language: 'English (Australia)', gender: 'Male', flag: '🇦🇺' },
  Emma: { language: 'English (UK)', gender: 'Female', flag: '🇬🇧' },
  Nicole: { language: 'English (Australia)', gender: 'Female', flag: '🇦🇺' },
  Olivia: { language: 'English (New Zealand)', gender: 'Female', flag: '🇳🇿' },
  Joanna: { language: 'English (US)', gender: 'Female', flag: '🇺🇸' },
  Kimberly: { language: 'English (US)', gender: 'Female', flag: '🇺🇸' },
  Kendra: { language: 'English (US)', gender: 'Female', flag: '🇺🇸' },
  Salli: { language: 'English (US)', gender: 'Female', flag: '🇺🇸' },
  Joey: { language: 'English (US)', gender: 'Male', flag: '🇺🇸' },
  Justin: { language: 'English (US)', gender: 'Male', flag: '🇺🇸' },
  Brian: { language: 'English (UK)', gender: 'Male', flag: '🇬🇧' },
};

/**
 * Formats a Polly voice name to human-readable: "Aria — 🇳🇿 English (New Zealand), Female"
 */
export function formatPollyVoice(voiceName: string | undefined): string {
  if (!voiceName) return '—';
  const meta = POLLY_VOICES[voiceName];
  if (!meta) return voiceName;
  return `${voiceName} — ${meta.flag} ${meta.language}, ${meta.gender}`;
}

/**
 * Formats a boolean field to On/Off with colour hint.
 */
export function formatOnOff(value: unknown): { text: string; colour: 'green' | 'red' | 'grey' } {
  if (value === true || value === 'true') return { text: 'On', colour: 'green' };
  if (value === false || value === 'false') return { text: 'Off', colour: 'red' };
  return { text: '—', colour: 'grey' };
}
